import { generatePath, Link } from "react-router-dom"
import { Button, Tag, Typography } from "antd"
import { ColumnsType } from "antd/es/table"

import { GlobalCurrenciesCollection } from "@src/app/CurrenciesCollection"
import { paths } from "@src/app/paths"
import { PaymentsEntity } from "@src/app/paymentsStore/PaymentsEntity"
import { router } from "@src/pages"
import { PaymentRefundEntity } from "@src/pages/refund/classes/PaymentRefundEntity"
import { GRefundPage } from "@src/pages/refund/classes/RefundPage"
import {
  MassPayoutShort,
  Payment,
  PaymentAmlLevelEnum,
  PaymentPricingTypeEnum,
  TopupPaymentResponseStatusEnum,
} from "@src/shared/api/generated"
import { DateTimeTableCell } from "@src/shared/componemts/DateTimeTableCell"
import {
  filtersPresets,
  getFilterSimpleFiled,
} from "@src/shared/componemts/TableFilter/classes/constants"
import { WithCopy } from "@src/shared/componemts/WithCopy"
import { getExplorerUrl } from "@src/shared/getExplorerUrl"
import { paddingLimit } from "@src/shared/strings"

export const PaymentsOutColumns: ColumnsType<Payment> = [
  {
    dataIndex: "id",
    render: (_, { id }) => (
      <WithCopy content={id} view={<>{paddingLimit(id, 0, 3)}</>} />
    ),
    title: "ID",
  },
  {
    render: (_, { charge_id }) => (
      <WithCopy
        content={charge_id}
        view={<>{paddingLimit(charge_id, 0, 3)}</>}
      />
    ),
    title: "Charge ID",
  },
  {
    render: (_, { address }) => {
      return paddingLimit(address, 0, 3)
    },
    title: "Address",
  },
  {
    render: (_, topUp) => {
      const operationWithUrl = getExplorerUrl(
        [topUp],
        GlobalCurrenciesCollection().items,
      )[0]

      return (
        <WithCopy
          content={topUp.tx_id}
          view={
            <Typography.Link
              href={operationWithUrl.tx_explorer_url + topUp.tx_id}
              style={{ fontSize: 12, whiteSpace: "nowrap" }}
              target="_blank"
            >
              {paddingLimit(topUp.tx_id, 0, 3)}
            </Typography.Link>
          }
        />
      )
    },
    title: "Tx ID",
    width: 120,
  },
  {
    dataIndex: "amount",
    render: (_, { amount }) => {
      return <b>{amount}</b>
    },
    title: "Amount",
  },
  {
    dataIndex: "symbol",
    render: (_, { network, symbol }) => {
      const currency = GlobalCurrenciesCollection().getBySymbolNetwork({
        network,
        symbol,
      })

      return currency?.symbol_alias ?? symbol
    },
    title: "Symbol",
  },
  {
    dataIndex: "network",
    title: "Network",
  },

  {
    dataIndex: "pricing_type",
    render: (_, { pricing_type }) => {
      const i = PAYMENTS_PRICING_TYPES_OPTIONS.find(
        (j) => j.value === pricing_type,
      )

      return i?.label ?? pricing_type
    },
    title: "Pricing type",
  },
  {
    render: (_, { status }) => {
      const cMap = {
        [TopupPaymentResponseStatusEnum.Confirmed]: "green",
        [TopupPaymentResponseStatusEnum.Failed]: "error",
        [TopupPaymentResponseStatusEnum.Pending]: "geekblue",
      }

      return <Tag color={cMap[status] ?? "geekblue"}>{status}</Tag>
    },
    title: "Status",
  },
  {
    dataIndex: "aml_level",
    render: (_, { aml_level }) => {
      const cMap = {
        [PaymentAmlLevelEnum.High]: "error",
        [PaymentAmlLevelEnum.Low]: "green",
        [PaymentAmlLevelEnum.Medium]: "warning",
      }

      return <Tag color={cMap[aml_level ?? "low"]}>{aml_level}</Tag>
    },
    title: "AML Level",
  },
  {
    dataIndex: "detected_at",
    render: (_, { detected_at }) => <DateTimeTableCell value={detected_at} />,
    title: "Detected At",
    width: 120,
  },
  {
    align: "center",
    dataIndex: "action",
    fixed: "right",
    render: (_, payment) => {
      const canRefund = payment.status === "confirmed" && !payment.refund_id

      if (!canRefund) return null

      const makeRefund = () => {
        GRefundPage().setEntity(
          new PaymentRefundEntity(new PaymentsEntity(payment)),
        )

        router.navigate(paths.refund)
      }

      return (
        <Button
          onClick={makeRefund}
          size="small"
          style={{
            borderColor: "#27582f",
            fontSize: 12,
            height: 32,
            padding: 4,
          }}
        >
          Refund
        </Button>
      )
    },
    width: 96,
  },
]

export const PaymentsFilter = [
  {
    ...getFilterSimpleFiled("address").search({
      col: 6,
      label: "Address",
      placeholder: "Search by address",
    }),
  },
  {
    ...getFilterSimpleFiled("id").search({
      col: 6,
      label: "ID",
      placeholder: "Search by ID",
    }),
  },
  {
    ...getFilterSimpleFiled("chargeId").search({
      col: 6,
      label: "Charge ID",
      placeholder: "Search by Charge ID",
    }),
  },
  {
    ...getFilterSimpleFiled("txId").search({
      col: 6,
      label: "Tx ID",
      placeholder: "Search by Tx ID",
    }),
  },
  { ...filtersPresets.order, col: 6 },
  { ...filtersPresets.status, col: 6 },
  {
    ...getFilterSimpleFiled("amlLevel").select(
      [
        { label: "High", value: "high" },
        { label: "Medium", value: "medium" },
        { label: "Low", value: "low" },
      ],
      {
        col: 6,
        label: "AML level",
        placeholder: "Select AML level",
      },
    ),
  },
  {
    ...getFilterSimpleFiled("pricingType").select(
      [
        { label: "Fixed price", value: PaymentPricingTypeEnum.FixedPrice },
        { label: "No price", value: PaymentPricingTypeEnum.NoPrice },
        // { label: "Deposit", value: PaymentPricingTypeEnum.Deposit },
        // { label: "Top up", value: PaymentPricingTypeEnum.Topup },
      ],
      {
        col: 6,
        label: "Pricing Type",
        placeholder: "Select pricing type",
      },
    ),
  },
]

export const PAYMENTS_PRICING_TYPES_OPTIONS = [
  { label: "Fixed price", value: PaymentPricingTypeEnum.FixedPrice },
  { label: "No price", value: PaymentPricingTypeEnum.NoPrice },
]
